/**
 * Embeds the SHXRE Movies app (Netflix clone in shxre-movies/) via WebView / iframe.
 * Deployed with SHXRE: same origin, no localhost.
 * - Web: loads from same origin at path /movies-app (e.g. https://shxre.net/movies-app).
 * - Native: set EXPO_PUBLIC_SHXRE_MOVIES_APP_URL to full URL (e.g. https://shxre.net/movies-app) at build time.
 */

import {View, StyleSheet} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {atoms as a, useTheme} from '#/alf'
import {IS_WEB} from '#/env'

/** Path where the Movies app is served when deployed with SHXRE (same origin). */
const MOVIES_APP_PATH = '/movies-app'

function getMoviesAppUrl(): string {
  if (IS_WEB && typeof window !== 'undefined') {
    return `${window.location.origin}${MOVIES_APP_PATH}`
  }
  return process.env.EXPO_PUBLIC_SHXRE_MOVIES_APP_URL ?? ''
}

function MoviesAppEmbedNative() {
  const WebView = require('react-native-webview').WebView
  const url = getMoviesAppUrl()
  if (!url) {
    return (
      <View style={[a.flex_1, a.justify_center, a.align_center, a.p_lg]}>
        <Text style={[a.text_center, a.text_md]}>
          <Trans>Set EXPO_PUBLIC_SHXRE_MOVIES_APP_URL when building the app.</Trans>
        </Text>
      </View>
    )
  }
  return (
    <View style={[a.flex_1, {backgroundColor: '#000'}]}>
      <WebView
        source={{uri: url}}
        style={StyleSheet.absoluteFill}
        javaScriptEnabled
        domStorageEnabled
        startInLoadingState
        scalesPageToFit
      />
    </View>
  )
}

function MoviesAppEmbedWeb() {
  const url = getMoviesAppUrl()
  return (
    <View style={[a.flex_1, {minHeight: '100vh', backgroundColor: '#000'}]}>
      <iframe
        title="SHXRE Movies"
        src={url}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </View>
  )
}

export function MoviesAppEmbedScreen() {
  const t = useTheme()
  const {_} = useLingui()
  return (
    <Layout.Screen testID="moviesAppEmbedScreen">
      {/* Thin bar with Back so user can return to SHXRE; the clone app has its own full UI below */}
      <Layout.Header.Outer>
        <Layout.Header.BackButton label={_(msg`Back to SHXRE`)} />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>SHXRE Movies</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      {IS_WEB ? <MoviesAppEmbedWeb /> : <MoviesAppEmbedNative />}
    </Layout.Screen>
  )
}
